/**
 * The Attribution Models table's channel drill-down: one row per campaign,
 * adset and ad, with each model's credit in its own column.
 *
 * The endpoint returns a separate campaign → adset → ad tree per model, and
 * models credit different ads, so the rows are the union of every model's
 * nodes. A node a model never credited reads as zero in that model's column.
 */

/** Channels whose touches can be matched to an ad_spend campaign. */
export const DRILLABLE_CHANNELS = ['meta', 'google', 'paid_branded_search', 'reddit', 'bing', 'tiktok']

const ZERO = { orders: 0, revenue: 0, new_orders: 0 }

function mergeLevel(target, nodes, modelId, { label, childKey }) {
  for (const node of nodes || []) {
    if (!target.has(node.key)) {
      target.set(node.key, { key: node.key, label: label(node), byModel: {}, children: new Map() })
    }
    const row = target.get(node.key)
    row.byModel[modelId] = node
    if (childKey) mergeLevel(row.children, node[childKey], modelId, LEVELS[childKey])
  }
}

const LEVELS = {
  campaigns: { label: node => node.campaign_name || node.campaign_id || 'Unnamed campaign', childKey: 'adsets' },
  adsets: { label: node => node.adset_name || node.adset_id || 'Unnamed adset', childKey: 'ads' },
  ads: { label: node => node.ad_name || node.ad_id || 'Unnamed ad', childKey: null },
}

/** Rows sorted by the largest credit any model gave them, biggest first. */
function sortRows(map, metric) {
  const peak = row => Math.max(0, ...Object.values(row.byModel).map(node => node[metric] || 0))
  return [...map.values()]
    .map(row => ({ ...row, children: sortRows(row.children, metric) }))
    .sort((a, b) => peak(b) - peak(a))
}

/**
 * @param {object} data - /attribution-models/campaigns response data
 * @param {string} metric - 'revenue' or 'orders', for ordering
 * @returns {{campaigns: object[], unmatched: object}} each row has byModel[modelId]
 */
export function mergeCampaignDrill(data, metric = 'revenue') {
  const campaigns = new Map()
  const unmatched = {}
  for (const model of data?.models || []) {
    mergeLevel(campaigns, model.campaigns, model.id, LEVELS.campaigns)
    unmatched[model.id] = model.unmatched || ZERO
  }
  return { campaigns: sortRows(campaigns, metric === 'both' ? 'revenue' : metric), unmatched }
}

/** A node's figure for one model and metric; zero when the model never credited it. */
export function drillValue(row, modelId, metric) {
  return row.byModel[modelId]?.[metric] || 0
}
